/**
 * lib/offer-href.ts — the ONE place an `/r/{offerId}` affiliate href is built.
 *
 * FRIDGE-276 (click-id coverage). Every outbound affiliate click is a row in
 * `clicks`; a row without a `cid` cannot be joined back to a page, module or
 * visitor when a network reports the sale. The `cid` is minted client-side by
 * `SignedOfferLink` (components/product/signed-offer-link.tsx), so a click
 * only carries one when its anchor was RENDERED by that component. Ad-hoc
 * string builders (`` `/r/${id}?product_id=…` ``) sprinkled through render
 * files silently opt out of the cid — that is how coverage sat at 12.5%.
 *
 * Contract, pinned by tests/offer-href-single-source.test.ts:
 *   - No file under app/, components/ or lib/ builds an `/r/` path string
 *     except this one. Render surfaces use <SignedOfferLink>; non-render
 *     callers (structured data, admin API, link-health probes) call
 *     `buildOfferHref` so the URL shape has exactly one author.
 *   - Param order is stable — product_id, pos, sp, cid, ts, hmac, cx — so the
 *     client-side cid rewrite only ever APPENDS to the server-rendered href.
 *
 * This module is deliberately framework-free (no 'use client', no React) so
 * server components, route handlers and routines can import it directly. A
 * function exported from a 'use client' module becomes a client reference on
 * the server and throws when called there.
 */

export interface OfferHrefOptions {
  productId?: string | null
  /** On-page position for per-module attribution (buybox, sticky, verdict,
   *  compare-col-N, reviews-inline, fbt, …). Attribution-only — not part of
   *  the HMAC base, so a pos never invalidates a signature. */
  pos?: string | null
  /**
   * The path of the page this link is rendered on — `?sp=` (FRIDGE-312).
   *
   * `clicks.source_page` is derived from the Referer header, and the header is
   * simply absent on 1,648 of the 1,866 unattributed human clicks in the
   * trailing 30 days (referrer-policy, privacy proxies, extensions). This is
   * the client's own declaration of the page, used ONLY where the header named
   * nothing (`lib/click-source-page.ts`). Client-supplied and analytics-only,
   * exactly like `pos`; never part of the HMAC base, so it cannot invalidate a
   * signature and cannot be used to claim provenance.
   */
  sourcePage?: string | null
  cid?: string | null
  /** Signature pair minted by /api/r-sign. Both or neither. */
  ts?: number | string | null
  hmac?: string | null
  /**
   * The server-signed click context the visitor arrived with — `?cx=`
   * (FRIDGE-818, lib/click-context.ts). Reporting-only, like `pos` and `sp`:
   * never part of the HMAC base, never able to change the destination or the
   * affiliate identity. Appended LAST so every earlier rewrite stays a prefix.
   */
  clickContext?: string | null
}

export function buildOfferHref(offerId: string, opts: OfferHrefOptions = {}): string {
  const params = new URLSearchParams()
  if (opts.productId) params.set('product_id', opts.productId)
  if (opts.pos) params.set('pos', opts.pos)
  // `sp` sits before `cid` for the same reason the order is fixed at all: both
  // are minted in the mount effect, so the server-rendered href (product_id +
  // pos) only ever grows to the right — the rewrite APPENDS, never reorders.
  if (opts.sourcePage) params.set('sp', opts.sourcePage)
  if (opts.cid) params.set('cid', opts.cid)
  if (opts.ts !== undefined && opts.ts !== null && opts.ts !== '' && opts.hmac) {
    params.set('ts', String(opts.ts))
    params.set('hmac', opts.hmac)
  }
  if (opts.clickContext) params.set('cx', opts.clickContext)
  const qs = params.toString()
  return `/r/${offerId}${qs ? `?${qs}` : ''}`
}
